import React, { useEffect, useState, useCallback } from "react";
import { fetchBoostStatus, castBoost, startSuperboostCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * BoostBar — lightning-bolt boost CTA + recent boost counter shown on
 * every LeagueDetailModal and as a chip on /leagues cards. Wired to:
 *   - POST /api/leagues/:id/boost      → spend 1 daily boost
 *   - POST /api/leagues/:id/superboost-checkout → 24h pin via Stripe ($0.99)
 *
 * `compact` mode renders just the chip (used inside league cards).
 */
export function BoostBar({ leagueId, compact = false, onToast }) {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId || !user) return;
    try {
      const s = await fetchBoostStatus(leagueId);
      setStatus(s);
    } catch {
      /* not authed or league gone — silent */
    }
  }, [leagueId, user]);

  useEffect(() => { load(); }, [load]);

  if (!user || !leagueId) {
    // Compact: still show the score chip with 0 if unauth so cards aren't blank.
    return null;
  }
  if (!status) return null;

  const cantBoost = status.boosts_remaining <= 0 || status.already_boosted_this_league;
  const cantReason = status.already_boosted_this_league
    ? "You've already boosted this league in the last 24h."
    : status.boosts_remaining <= 0
      ? (status.is_pro ? "No boosts left for the next 24h." : "Out of boosts — upgrade to PRO for 2/day.")
      : "";

  const onBoost = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await castBoost(leagueId);
      onToast?.(`⚡ Boosted! +1 to this league (${r.recent_boost_score} today).`);
      await load();
      await refresh?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Boost failed.");
    } finally { setBusy(false); }
  };

  const onSuperboost = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await startSuperboostCheckout(leagueId);
      window.location.href = r.url;
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't start checkout.");
      setBusy(false);
    }
  };

  // Compact: small inline chip used on league list cards.
  if (compact) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBoost(); }}
        disabled={busy || cantBoost}
        title={cantBoost ? cantReason : "Boost this league (+1)"}
        data-testid={`league-boost-compact-${leagueId}`}
        className={`inline-flex items-center gap-1 text-[10px] tracking-widest font-bold px-2 py-1 rounded-md border transition-colors ${
          status.superboost_active
            ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6] animate-glow-pulse"
            : "bg-black/40 border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/15"
        } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <i className="fas fa-bolt" style={{ filter: "drop-shadow(0 0 4px #14b8a6)" }} />
        {status.superboost_active ? "SUPER" : status.recent_boost_score}
      </button>
    );
  }

  return (
    <div
      data-testid={`league-boost-bar-${leagueId}`}
      className="glass rounded-2xl p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-9 h-9 rounded-xl bg-[#14b8a6]/15 border border-[#14b8a6]/50 flex items-center justify-center text-[#14b8a6] text-base"
              style={{ boxShadow: "0 0 14px rgba(253,224,71,0.45)" }}>
          <i className="fas fa-bolt" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            LEAGUE BOOST
            {status.superboost_active && (
              <span data-testid={`league-superboost-active-${leagueId}`}
                    className="ml-2 inline-block bg-[#14b8a6] text-black font-bold px-1.5 py-[1px] rounded">
                SUPERBOOSTED
              </span>
            )}
          </p>
          <p className="text-[11px] text-white/70">
            <span data-testid={`league-boost-score-${leagueId}`}>{status.recent_boost_score}</span>
            {` ${status.recent_boost_score === 1 ? "boost" : "boosts"} today • ${status.is_pro ? "PRO" : "FREE"}: `}
            <span className="text-[#14b8a6]">{status.boosts_remaining}/{status.boosts_per_window}</span> left
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onBoost}
          disabled={busy || cantBoost}
          data-testid={`league-boost-btn-${leagueId}`}
          title={cantBoost ? cantReason : "Boost this league (+1)"}
          className="px-3 py-2 rounded-xl text-[11px] tracking-widest font-bold bg-[#14b8a6]/20 border border-[#14b8a6] text-[#14b8a6] hover:bg-[#14b8a6]/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="fas fa-bolt mr-1" />BOOST
        </button>
        <button
          type="button"
          onClick={onSuperboost}
          disabled={busy}
          data-testid={`league-superboost-btn-${leagueId}`}
          title="Pin this league to the top of /leagues for 24 hours ($0.99)"
          className="px-3 py-2 rounded-xl text-[11px] tracking-widest font-bold bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-black hover:brightness-110 disabled:opacity-50"
        >
          <i className="fas fa-bolt mr-1" />SUPERBOOST · $0.99
        </button>
      </div>
    </div>
  );
}

export default BoostBar;
